import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    RaceDashboardFilter,
    RaceDistributionDto,
    RaceYearTotalDto,
    RaceGrandTotalDto
} from 'generated/openApi';
import ApiService from 'services/apiService';

interface RaceDataContextType {
    data: RaceDistributionDto[];
    loading: boolean;
    error: string | null;
    filter: RaceDashboardFilter;
    activeSchoolYear: number | null;
    totalsByYear: RaceYearTotalDto[];
    grandTotal: RaceGrandTotalDto | null;
    updateFilter: (filter: Partial<RaceDashboardFilter>) => void;
    refetch: () => void;
}

const RaceDataContext = createContext<RaceDataContextType | undefined>(undefined);

export const RaceDataProvider = ({ children }: { children: React.ReactNode }) => {
    const [data, setData] = useState<RaceDistributionDto[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<RaceYearTotalDto[]>([]);
    const [grandTotal, setGrandTotal] = useState<RaceGrandTotalDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<RaceDashboardFilter>({
        schoolYears: [],
    });
    const [initialized, setInitialized] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const responseData = await ApiService.raceDashboard.getRaceDistribution(filter);

            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);

                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(prev => ({
                        ...prev,
                        schoolYears: [responseData.activeSchoolYear]
                    }));
                    setInitialized(true);
                    return;
                }
            }

            setData(responseData.data || []);
            setTotalsByYear(responseData.totalsByYear || []);
            setGrandTotal(responseData.grandTotal || null);
        } catch (err) {
            setError('Failed to load race data');
            console.error('Error fetching race data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<RaceDashboardFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    };

    const refetch = () => {
        fetchData();
    };

    return (
        <RaceDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                activeSchoolYear,
                totalsByYear,
                grandTotal,
                updateFilter,
                refetch,
            }}
        >
            {children}
        </RaceDataContext.Provider>
    );
};

export const useRaceData = () => {
    const context = useContext(RaceDataContext);
    if (!context) {
        throw new Error('useRaceData must be used within RaceDataProvider');
    }
    return context;
};